import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useEffect, useMemo, useState } from 'react';

import type {
  FilterChangedEvent,
  GetQuickFilterTextParams,
  GridApi,
  IRowNode,
  RowSelectionOptions,
  SelectionChangedEvent,
} from '@mint-lib/ag-grid/community';
import {
  ClientSideRowModelModule,
  ModuleRegistry,
  QuickFilterModule,
  RowApiModule,
  RowAutoHeightModule,
  RowSelectionModule,
  ValidationModule,
} from '@mint-lib/ag-grid/community';
import { LoadingCell } from '@mint-lib/ag-grid/components';
import {
  CellSelectionModule,
  LicenseManager,
  RowGroupingModule,
  TreeDataModule,
} from '@mint-lib/ag-grid/enterprise';
import { useTranslation } from '@mint-lib/i18n';

import { useTags } from '../api/queries/tags.js';
import { useTagsPlanning } from '../api/queries/tagsPlanning.js';
import { includeSelectedParents } from '../utils/selectionParents.js';
import { LabelCell } from './LabelCell.jsx';
import { LabelsSearch } from './LabelsSearch.jsx';
import { LabelsTabs } from './LabelsTabs.jsx';
import { NoResultsOverlay } from './NoResultsOverlay.jsx';

ModuleRegistry.registerModules([
  RowGroupingModule,
  RowAutoHeightModule,
  ValidationModule,
  ClientSideRowModelModule,
  TreeDataModule,
  CellSelectionModule,
  RowApiModule,
  QuickFilterModule,
  RowSelectionModule,
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

export type LabelsTableProps = {
  isReadonlyPermission: boolean;
  isSaving: boolean;
  assignedTags: { id: string }[];
  isMint2: boolean;
  initialCategoryType?: string;
  onCategoryChange?: (categoryType: string) => void;
  onTagsAssign: (tags: { id: string }[]) => void;
  isPlanning?: boolean;
  visibleCategoryNames?: string[];
};

export const LabelsTable = ({
  isReadonlyPermission,
  isSaving,
  assignedTags,
  isMint2,
  initialCategoryType,
  onCategoryChange,
  onTagsAssign,
  isPlanning,
  visibleCategoryNames,
}: LabelsTableProps) => {
  const { t } = useTranslation('@mint-lib/labels-table');

  const categoryTypeInitialBasedOnMintVersion = isMint2 ? '' : 'brands';
  const [categoryType, setCategoryType] = useState<string>(
    initialCategoryType || categoryTypeInitialBasedOnMintVersion,
  );

  const useTagsFunc = isPlanning ? useTagsPlanning : useTags;
  const tagsData = useTagsFunc({
    isMint2,
    categoryType,
    isPlanning,
    assignedTags,
    visibleCategoryNames,
  });

  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (
      isMint2 &&
      (!categoryType || categoryType === '') &&
      tagsData.tabs?.[0]
    ) {
      setCategoryType(tagsData.tabs[0].name);
      onCategoryChange?.(tagsData.tabs[0].name);
    }
  }, [tagsData.tabs, isMint2, categoryType]);

  const handleCategoryChange = (categoryType: string) => {
    onCategoryChange?.(categoryType);
    setCategoryType(categoryType);
  };

  const categoryTitle = useMemo(() => {
    if (isMint2) {
      return categoryType;
    }
    return categoryType === 'brands'
      ? t('Brands & Products')
      : t('Geographies');
  }, [categoryType, isMint2, t]);

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: categoryTitle,
      field: 'name',
      flex: 1,
      sortable: false,
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: LabelCell,
      },
      loadingCellRenderer: LoadingCell,
      getQuickFilterText: (params: GetQuickFilterTextParams) => {
        if (!params.value) return '';
        if (!params.data.category) return '';
        return params.value.toLowerCase();
      },
    };
  }, [categoryTitle]);

  const rowSelection = useMemo<RowSelectionOptions>(() => {
    return {
      mode: 'multiRow',
      enableClickSelection: false,
      headerCheckbox: !isReadonlyPermission && !isPlanning,
      checkboxes: (params) => {
        return (
          !!params?.node?.data?.category &&
          !isReadonlyPermission &&
          !params.data.isDisabled
        );
      },
      checkboxLocation: 'autoGroupColumn',
      isRowSelectable: (node: IRowNode) => {
        return !!node?.data?.category;
      },
      groupSelects: 'descendants',
    };
  }, [isReadonlyPermission]);

  const onRowDataUpdated = (params: { api: GridApi }) => {
    const nodesToSelect: IRowNode[] = [];

    params.api.forEachNode((node) => {
      if (
        node.data?.id !== undefined &&
        assignedTags.some((tag: { id: string }) => tag.id === node.data.id)
      ) {
        nodesToSelect.push(node);
      }
    });

    params.api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    const arraySelectedTags = includeSelectedParents(event.selectedNodes ?? []);

    if (event.source === 'checkboxSelected') {
      onTagsAssign?.(arraySelectedTags ?? []);
    }

    if (event.source === 'uiSelectAll' && !isPlanning) {
      onTagsAssign?.(arraySelectedTags ?? []);
    }
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    if (params.api.getDisplayedRowCount() === 0) {
      params.api.showNoRowsOverlay();
    } else {
      params.api.hideOverlay();
    }
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-col gap-2">
        <LabelsTabs
          isMint2={isMint2}
          categoryType={categoryType}
          setCategoryType={handleCategoryChange}
          setSearchValue={setSearchValue}
          tabs={tagsData.tabs}
        />
        <LabelsSearch
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
      </div>
      <div className="h-[505px]">
        <AgGridReact
          rowData={tagsData.nodesMap}
          getRowId={(row) => row.data.id ?? row.data.name}
          treeData={true}
          loadThemeGoogleFonts
          cellSelection={true}
          treeDataChildrenField="children"
          rowSelection={rowSelection}
          autoGroupColumnDef={autoGroupColumnDef}
          loading={isSaving || tagsData.isLoading}
          columnDefs={[
            {
              field: 'name',
              hide: true,
            },
          ]}
          defaultColDef={{
            suppressHeaderMenuButton: true,
            resizable: false,
            sortable: false,
            flex: 1,
          }}
          onSelectionChanged={onSelectionChanged}
          quickFilterText={searchValue}
          onFilterChanged={onFilterChanged}
          onRowDataUpdated={onRowDataUpdated}
          noRowsOverlayComponent={NoResultsOverlay}
        />
      </div>
    </div>
  );
};
